import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { acceptInvite } from '../api';
import { useAuth } from '../context/AuthContext';
import { AuthCard, PasswordField } from '../components/AuthCard';
import useEmailLinkToken from '../hooks/useEmailLinkToken';
import { PASSWORD_HINT, passwordProblem } from '../utils/emailLinks';

// The token check may name the workspace as a string or as { id, name }.
const workspaceName = (workspace) => (typeof workspace === 'string' ? workspace : workspace?.name) || '';

function AcceptInvite() {
  const { token, status, details, error: linkError, retry } = useEmailLinkToken('invite');
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    const problem = passwordProblem(password);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    try {
      await acceptInvite(token, password);
      // The server has signed this person in; load their session and go in.
      await checkAuth();
      navigate('/', { replace: true });
    } catch (err) {
      if (err.response?.data?.workspaceUnavailable) {
        setError(err.response.data.error || 'This workspace is paused. Contact your administrator.');
      } else {
        setError(err.response?.data?.error || 'Could not set your password. Try again.');
      }
      setSaving(false);
    }
  };

  const name = details?.name || '';
  const workspace = workspaceName(details?.workspace);

  const renderBody = () => {
    if (status === 'checking') {
      return <div className="loading-container" aria-label="Checking your invite"><div className="spinner"></div></div>;
    }

    if (status === 'failed') {
      return (
        <>
          <div className="alert alert-error" role="alert">{linkError}</div>
          <button type="button" className="btn btn-primary login-btn" onClick={retry}>Try again</button>
        </>
      );
    }

    if (status === 'invalid') {
      return (
        <>
          <div className="alert alert-error" role="alert">{linkError}</div>
          <p className="login-text">Invites work once and expire after a while. Ask your administrator to send you a new one. If you already set your password, sign in instead.</p>
          <Link to="/login" className="btn btn-primary login-btn">Go to sign in</Link>
        </>
      );
    }

    return (
      <>
        <p className="login-text">
          {name ? <>Welcome, <strong>{name}</strong>. </> : 'Welcome. '}
          {workspace
            ? <>Set a password to join <strong>{workspace}</strong> on ActiveOptics.</>
            : 'Set a password to finish setting up your account.'}
        </p>
        {error && <div className="alert alert-error" role="alert" aria-live="assertive">{error}</div>}
        <form onSubmit={handleSubmit} noValidate>
          {details?.email && (
            <div className="form-group">
              <label className="form-label" htmlFor="invite-email">Email</label>
              <input id="invite-email" type="email" className="form-input" value={details.email} autoComplete="username" readOnly />
            </div>
          )}
          <PasswordField id="invite-password" label="Password" value={password} onChange={setPassword} placeholder="Choose a password" describedBy="invite-password-hint" />
          <p id="invite-password-hint" className="login-hint">{PASSWORD_HINT}</p>
          <PasswordField id="invite-password-confirm" label="Confirm password" value={confirmPassword} onChange={setConfirmPassword} placeholder="Confirm your password" />
          <button type="submit" className="btn btn-primary login-btn" disabled={saving}>
            {saving ? <span>Setting up...</span> : (<><LogIn size={18} /><span>Set password and sign in</span></>)}
          </button>
        </form>
      </>
    );
  };

  return (
    <AuthCard subtitle="Accept your invite">
      {renderBody()}
    </AuthCard>
  );
}

export default AcceptInvite;
